import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { isEqual } from 'lodash';
import { useParams } from 'react-router-dom';

import Yup from '../../utils/yup';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import addNotification from '../../utils/addNotification';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import { NotificationType } from '../../config';
import masterDataService from '../../service/masterData.service';
import assemblyDefinitionService from '../../service/assemblyDefinition.service';
import { AssemblyDefinitionFields } from './AssemblyDefinitionFields';
import { AssemblyDefinitionLine, mapAssemblyDefinitionLinesForSubmit } from './AssemblyDefinitionLine';
import { AssemblyDefinitionReports } from './AssemblyDefinitionReports';

const def = {
  itemCode: null,
  note: '',
  sourcePriceList: null,
  item: null,
  priceList: null,
  assemblyDefinitionLine: [],
};

const defaultLine = {
  itemCode: null,
  qty: 1,
  item: null,
  quantityUnitId: null,
};

export const AssemblyDefinitionPage = () => {
  const { id } = useParams();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getUser, user } = useUsers();

  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [defaultValues, setDefaultValues] = useState(def);
  const [selectedValues, setSelectedValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState(def);
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [initValues, setInitValues] = useState(def);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autoCompleteData, setAutoCompleteData] = useState({
    priceList: [],
    quantityUnit: [],
  });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    priceList: [],
  });

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) {
      getUser();
    }

    setDefaultValues(def);
    setDefaultSelectedValues(def);

    Promise.all([masterDataService.priceListToForm(), masterDataService.quantityUnitToForm()])
      .then(([priceListData, quantityUnitData]) => {
        setAutoCompleteData({
          priceList: priceListData,
          quantityUnit: quantityUnitData,
        });
        setAutoCompleteOptions({
          priceList: generateAutocompleteItems(priceListData, 'name', 'id'),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('assemblyDefinition');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (!id) {
      return;
    }

    assemblyDefinitionService.getItemById(id).then((data) => {
      setSelectedValues(data);
    });
  }, [id]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [defaultSelectedValues, defaultValues, selectedValues]);

  const validationSchema = Yup.object().shape({
    itemCode: Yup.string().required(),
    note: Yup.string().nullable(),
    sourcePriceList: Yup.number().required(),
    assemblyDefinitionLine: Yup.array()
      .of(
        Yup.object().shape({
          itemCode: Yup.string().required(),
          qty: Yup.number().required().positive(),
        })
      )
      .min(1, 'Legalább egy tétel szükséges!')
      .required(),
  });

  const valuesToForm = () => {
    const { itemCode, note, sourcePriceList, item, priceList, assemblyDefinitionLine } = selectedValues;

    setInitValues({
      id: selectedValues.id,
      itemCode,
      note: note ?? '',
      sourcePriceList,
      item: item ?? null,
      priceList: priceList ?? null,
      assemblyDefinitionLine:
        assemblyDefinitionLine?.map((line) => ({
          ...line,
          qty: line.qty ?? '',
          item: line.item ?? null,
          quantityUnitId: line.quantityUnitId ?? line.item?.quantityUnit?.id ?? line.item?.quantityUnitId ?? null,
        })) ?? [],
    });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = (values) => {
    const updData = {
      id: values.id,
      itemCode: values.itemCode,
      note: values.note ?? null,
      sourcePriceList: values.sourcePriceList,
      assemblyDefinitionLine: mapAssemblyDefinitionLinesForSubmit(values.assemblyDefinitionLine),
    };

    assemblyDefinitionService.updateItem(updData).then((data) => {
      assemblyDefinitionService.getItemById(data.id).then((loadedData) => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(loadedData);
        setRefetchTable(true);
        setEditButtonvisible(true);
        setViewField(true);
      });
    });
  };

  const handleInsertObject = (values) => {
    const insertObject = {
      itemCode: values.itemCode,
      note: values.note ?? null,
      sourcePriceList: values.sourcePriceList,
      assemblyDefinitionLine: mapAssemblyDefinitionLinesForSubmit(values.assemblyDefinitionLine).map((line) => ({
        itemCode: line.itemCode,
        qty: line.qty,
      })),
    };

    assemblyDefinitionService.createItem(insertObject).then((data) => {
      assemblyDefinitionService.getItemById(data.id).then((loadedData) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });

        setSelectedValues(loadedData);
        setRowSelectionModel([data.id]);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      });
    });
  };

  const handleRemoveElement = () => {
    assemblyDefinitionService.deleteItem(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });

      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues({});
      setInitValues(def);
    });
  };

  return (
    <>
      <FormBase
        name="AssemblyDefinition"
        leftPanelContent={
          <div className="h-full overflow-auto custom-scrollbar">
            <FormHeaderUI
              addClick={() => {
                setSelectedValues(defaultSelectedValues);
                setViewField(false);
                setAddField(false);
              }}
              addDisabled={!viewField || !canEdit}
              modClick={() => {
                setViewField(false);
                setEditButtonvisible(false);
              }}
              modDisabled={!viewField || operatorsDisabled || !canEdit}
              deleteClick={() => {
                setShowDeleteDialog(true);
              }}
              deleteDisabled={!viewField || operatorsDisabled || !canEdit}
              copyClick={() => {
                setViewField(false);
                setAddField(false);
              }}
              copyDisabled={!viewField || operatorsDisabled || !canEdit}
            />

            <Formik
              initialValues={initValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ resetForm }) => (
                <Form>
                  <AssemblyDefinitionFields
                    viewField={viewField}
                    selectedValues={selectedValues}
                    autoCompleteOptions={autoCompleteOptions}
                    autoCompleteData={autoCompleteData}
                  />
                  <AssemblyDefinitionLine
                    disabled={viewField}
                    defaultItem={defaultLine}
                    autocompleteData={autoCompleteData}
                  />

                  <FormButtonPanelUI
                    onCancelClick={() => {
                      setViewField(true);
                      setEditButtonvisible(true);
                      setAddField(true);
                      resetForm();
                    }}
                    disabled={editButtonVisible && addField}
                  />
                </Form>
              )}
            </Formik>
          </div>
        }
        rightPanelContent={
          !isLoading && (
            <AssemblyDefinitionReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
              autoCompleteOptions={autoCompleteOptions}
            />
          )
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
